import { ComponentType, ElementType } from 'react';
import { PlansPage, Deal } from './plans';
import { ComposerNotesDataType } from '@osmosis/shared/types/notes';
import { getPageByNodeID } from '../lib/db/pages';
import { MatrixProduct } from '../components/composer/blocks/MatrixColumns';
import { ProductProps } from '../types/products';

export type SideView = SideViewType | '';
export enum SideViewType {
  BLOCK_EDITOR = 'block-editor',
  ADD_BLOCK = 'add-block',
  PAGE_EDITOR = 'page-editor',
}

export enum ContributorType {
  AUTHOR = 'Author',
  EDITOR = 'Editor',
  ILLUSTRATOR = 'Illustrator',
  COPYEDITOR = 'Copyeditor',
}

export enum EditablePropsType {
  Text = 'TEXT',
  RichText = 'RICH_TEXT',
  Array = 'ARRAY',
  Object = 'OBJECT',
  Select = 'SELECT',
  Boolean = 'BOOLEAN',
  Media = 'MEDIA',
  Product = 'PRODUCT',
  ProductWithProgram = 'PRODUCT_WITH_PROGRAM',
  // Number = "NUMBER",
  // Date = "DATE",
  // Range = "RANGE",
  // Select = "SELECT",
  // Image = "IMAGE",
  // Custom = "CUSTOM"
}

export default EditablePropsType;

export type CTAProps = {
  caption: string;
  url: string;
  buttonColor?: string;
  tracking?: {
    type: 'generic';
  };
};
export type MovableCTAProps = {
  caption: string;
  url: string;
  buttonColor?: string;
  position: 'top' | 'bottom';
  tracking?: {
    type: 'generic';
  };
};

export type MediaProps = {
  type: string;
  src: string;
  altText?: string;
  alignment?: 'right' | 'left' | 'center';
  controls?: boolean | 'true' | 'false';
  autoplay?: boolean | 'true' | 'false';
  loop?: boolean | 'true' | 'false';
};

export type TextBannerProps = {
  heading?: string;
  subheading?: string;
  text?: string;
  theme?: string;
  cta?: CTAProps;
  ctas?: CTAProps[];
  topHeader?: boolean;
};

export type QuoteBlockProps = {
  heading?: string;
  subheading?: string;
  text?: string;
  theme?: string;
  cta?: CTAProps;
  ctas?: CTAProps[];
  anchor?: string;
  media?: MediaProps;
  student?: {
    name: string;
    school: string;
    image: string;
  };
  topHeader?: boolean;
};

export type TextProps = TextBannerProps & {
  style?: {
    backgroundImage?: string;
  };
};

export type AppStoreLinksBlockProps = {
  heading?: string;
  subheading?: string;
  text?: string;
  images?: ImageItem[];
  topHeader?: boolean;
};

export type PlanSelectorBlockProps = {
  heading?: string;
  subheading?: string;
  text?: string;
  theme?: string;
  plans?: PlansPage;
};

export type FeatureItem = {
  title: string;
  icon: {
    name: string;
    color: string;
  };
};

export type FeatureNewItem = {
  title: string;
  icon: { name: string };
};

export type FeatureListColumn = {
  title: string;
  subtitle?: string;
  features: FeatureItem[];
};

export type FeatureListNewColumn = {
  title: string;
  features: string;
  icon: {
    name: string;
  };
};

export type FeatureListBlockProps = {
  heading?: string;
  subheading?: string;
  text?: string;
  columns?: FeatureListColumn[][];
  cta?: CTAProps;
  ctas?: CTAProps[];
  theme?: string;
  anchor?: string;
  topHeader?: boolean;
};

export type FeatureListNewBlockProps = {
  heading?: string;
  subheading?: string;
  text?: string;
  columns?: FeatureListNewColumn[][];
  cta?: CTAProps;
  ctas?: CTAProps[];
  theme?: string;
  anchor?: string;
  topHeader?: boolean;
};

export type HeroBannerBlockProps = {
  heading?: string;
  subheading?: string;
  text?: string;
  media?: MediaProps;
  cta?: CTAProps;
  //   dynamicForm?: any; // TODO:
  theme?: string;
  anchor?: string;
  topHeader?: boolean;
  backgroundImage?: {
    image?: string;
  };
};

export type ImageItem = {
  justify: string;
  outline: boolean;
  image?: string;
  heading?: string;
  text?: string;
  tannerScriptHeading?: string;
  subheading?: string;
  url?: string;
  altText?: string;
};

export type AppLinkImageItem = {
  image?: string;
  heading?: string;
  url?: string;
  altText?: string;
};

export type ImageGridBlockProps = {
  title?: string;
  subtitle?: string;
  text?: string;
  cta?: CTAProps;
  ctas?: CTAProps[];
  theme?: string;
  anchor?: string;
  images?: ImageItem[];
  topHeader?: boolean;
  mcta?: MovableCTAProps;
  overrideBackground?: string;
};

type AlignmentPosition = 'center' | 'left' | 'right';

export type MediaAndTextBlockProps = {
  heading: string;
  media: MediaProps;
  subheading?: string;
  text?: string;
  cta?: CTAProps;
  ctas?: CTAProps[];
  theme?: string;
  anchor?: string;
  topHeader?: boolean;
};

export type MediaBannerBlockProps = MediaAndTextBlockProps & {
  headingAlignment: AlignmentPosition;
  headingTextColor: string;
  headingFontSize: string;
  subheadingAlignment: AlignmentPosition;
  subheadingTextColor: string;
  subheadingFontSize: string;
  isWordPressBlock: boolean;
  fontStyle: string;
  overrideBackground: string;
};

export type BaseBlockText = {
  heading?: string;
  subheading?: string;
  text?: string;
  topHeader?: boolean;
};

export type Book = {
  name: string;
  image: string;
  url: string;
};

export type BookDownloadBlockProps = BaseBlockText & {
  campaign?: {
    name: string;
    type: string;
  };
  dialogMessage?: string;
  books?: Book[];
  theme?: string;
  requiresAccount?: boolean;
  nodeID?: number;
};

export type MediaCarouselBlockProps = {
  cta?: CTAProps;
  ctas?: CTAProps[];
  topHeader?: boolean;
  media?: ({
    type: 'image';
    src: string;
    altText?: string;
  } & BaseBlockText & {
      cta: CTAProps;
      ctas: CTAProps[];
    })[];
  styles?: {
    backgroundImage?: string;
  };
  theme?: string;
  anchor?: string;
} & BaseBlockText;

export type MediaAndTextListBlockProps = {
  heading?: string;
  subheading?: string;
  text?: string;
  theme?: string;
  anchor?: string;
  list?: MediaAndTextBlockProps[];
  topHeader?: boolean;
};

export type TabContent = {
  image: ImageItem;
  illustrationPosition: 'left' | 'right';
  mainDefinition: TabMainDefinition;
  features: TabFeature[];
};
type TabFeature = {
  icon: string;
  text: string;
};

type TabMainDefinition = {
  icon: string;
  title: string;
  description: string;
};

export type TabNavigationBlockItem = {
  tabName: string;
  tabContent: TabContent;
};

export type TabNavigationBlockProps = {
  tabs: TabNavigationBlockItem[];
  theme?: string;
};

export type ComposerBlock = {
  name: string;
  id: string;
  args: Record<string, any>; // | AnotherBlock | SomeOtherBlock
  schema?: SchemaDefinition;
  component?: () => JSX.Element;

  // Legacy
  block?: string;
};

export type ComposerPageDataProps = {
  type?: ComposerNotesDataType;
  nextjs: boolean;
  title: string;
  image: string;
  description: string;
  content: {
    // path: string;
    blocks: ComposerBlock[];
  };
  neeedVisitorType?: boolean;
};

export enum PageVersion {
  A = 1,
  B = 2,
  C = 3,
}

export const PageVersionMap: { [index: number]: string } = {
  [PageVersion.A]: 'A',
  [PageVersion.B]: 'B',
  [PageVersion.C]: 'C',
};

export enum FilterNameProps {
  NEW_PAGES = 'newPages',
  DRAFTS = 'drafts',
  PUBLISHED = 'published',
  ANSWERS = 'answers',
}

export type FilterProps = {
  checked: boolean;
  label: string;
  id: FilterNameProps;
};

export enum ComposerPageStatus {
  DRAFT = 1,
  PUBLISHED = 2,
}

export type PagePropsByNodeID = Awaited<ReturnType<typeof getPageByNodeID>>;
export type ComposerPageProps = Omit<PagePropsByNodeID, 'nodeID'> &
  Partial<{
    nodeID: PagePropsByNodeID['nodeID'];
  }>;

export type SchemaDefinition = {
  name: string;
  label: string;
  description?: string;
  properties?: Record<string, SchemaDefinition | MediaSchemaDefinition>;
  items?: SchemaDefinition;
  selectOptions?: {
    options: SelectOption[];
  };
  type?: EditablePropsType;
  required?: boolean;
  editableProps?: SchemaDefinition[];
  defaultValue?: string | string[] | boolean | number | number[]; // Allow various default value types
  show?: (props: Record<string, any>) => boolean;
  topHeader?: boolean;
};

export type MediaSchemaDefinition = SchemaDefinition & {
  accept: string[]; // <input> array of element accept props
};

export type ObjectSchemaDefinition = SchemaDefinition & {
  properties: { [index: string]: SchemaDefinition };
};

export type SelectOption = {
  label: string;
  value: string;
};

export type SelectSchemaDefinition = SchemaDefinition & {
  selectOptions: {
    options: SelectOption[];
  };
};

export type ObjectProps = { [index: string]: ValueProps };

export type ValueProps = string | boolean | ObjectProps | ValueProps[];

export type UpdateValue = (
  blockId: string,
  key: string,
  value: ValueProps,
) => void;

export type SchemaProps = { [index: string]: ValueProps };

export type ArrayItemType = { [index: string]: any };

export type ComposerBlockElement = ElementType &
  ComponentType & {
    schema: SchemaDefinition;
  };

export type ComposerPlansElement = ComposerBlockElement & {
  getPlansParams?: (block: ComposerBlock) => {
    program?: string;
    planLengths?: string[];
    productIDs?: number[];
  };
  getProducts?: (
    blockProducts: MatrixProduct[],
    hostname: string,
  ) => ProductProps;
};

export type StatBlockProps = {
  title?: string;
  subtitle?: string;
  text?: string;
  topHeader?: boolean;
  stats?: StatItemProps[];
  theme?: string;
};

export type StatItemProps = {
  value?: string;
  type?: '%' | 'x' | '';
  title?: string;
  description?: string;
  statColor?: string;
  theme?: string;
};

export type Testimonial = {
  text: string;
  image: string;
  author: string;
};

export type TestimonialCarouselBlockProps = {
  heading?: string;
  subheading?: string;
  text?: string;
  testimonials: Testimonial[];
  backgroundColor?: string;
  interval?: number;
};

export type PlanSelectorProps = {
  heading?: string;
  subheading?: string;
  text?: string;
  theme?: string;
  plansPage?: {
    deal?: Deal;
  };
  monthlyPrice?: boolean;
  program?: string;
  program2?: string;
  productID?: number | string;
  productID2?: number | string;
  planLengths?: number[] | string[];
  planLengths2?: number[] | string[];
  planVersion?: string; // Added explicit planVersion
  planVersion2?: string; // Added explicit planVersion2
  dealCode?: string;
  dealType?: string;
  dealCodes?: PlanDealCode[];
  discountPills?: PlanDiscountPill[];
  dailyPrice?: boolean;
  fontStyle?: string;
  overrideBackground?: string;
  [key: string]: unknown; // For other potential props
};

export type PlanDealCode = {
  planLength: string | number;
  dealCode: string;
};

export type PlanDiscountPill = {
  planLength: string | number;
  text: string;
};

export type HeaderProps = {
  text: string;
  alignment?: AlignmentPosition;
  textColor?: string;
  theme?: string;
  textProps?: string;
  topHeader?: boolean;
  fontSize?: string;
  isWordPressBlock?: boolean;
};
